import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import FilterGroup from "@/components/feature/FilterGroup";
import SortDropdown from "@/components/feature/SortDropdown";
import VideoGrid from "@/components/base/VideoGrid";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { categories } from "@/mocks/categories";
import {
  applyFilters,
  DEFAULT_FILTERS,
  GENRE_OPTIONS,
  getVideosByType,
  paginate,
  REGION_OPTIONS,
  sortVideos,
  totalPages,
  YEAR_OPTIONS,
  type FilterState,
  type SortKey,
} from "@/lib/videoQuery";

const PAGE_SIZE = 12;

export default function CategoryPage() {
  const { type = "" } = useParams();
  const meta = categories.find((item) => item.type === type);

  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [sortKey, setSortKey] = useState<SortKey>("recommend");
  const [page, setPage] = useState(1);
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    setFilters(DEFAULT_FILTERS);
    setSortKey("recommend");
    setPage(1);
  }, [type]);

  useEffect(() => {
    setPage(1);
  }, [filters, sortKey]);

  const typeVideos = useMemo(() => getVideosByType(type), [type]);

  const filtered = useMemo(() => applyFilters(typeVideos, filters), [typeVideos, filters]);
  const sorted = useMemo(() => sortVideos(filtered, sortKey), [filtered, sortKey]);
  const total = totalPages(sorted.length, PAGE_SIZE);
  const paged = paginate(sorted, page, PAGE_SIZE);

  const updateFilter = (key: keyof FilterState, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => setFilters(DEFAULT_FILTERS);

  if (!meta) {
    return (
      <div className="mx-auto max-w-site px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-error-warning-line"
          title="未找到该分类"
          description="你访问的分类不存在，返回首页看看其他精彩内容吧。"
          action={
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
            >
              <i className="ri-home-4-line" />
              返回首页
            </Link>
          }
        />
      </div>
    );
  }

  const hasActiveFilter =
    filters.region !== "全部" || filters.year !== "全部" || filters.genre !== "全部";

  return (
    <div className="mx-auto max-w-site px-4 py-8 md:px-6 md:py-10">
      <nav className="mb-5 flex flex-wrap items-center gap-1.5 text-sm text-foreground-600" aria-label="面包屑导航">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line text-foreground-400" />
        <span className="text-foreground-900">{meta.name}</span>
      </nav>

      <header className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          {meta.name}
        </h1>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
          {meta.description}
        </p>
      </header>

      <div className="flex flex-col gap-8 lg:flex-row">
        <aside className="hidden w-60 shrink-0 lg:block">
          <div className="space-y-6 rounded-lg border border-background-200 bg-background-100 p-5">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-sm font-bold text-foreground-950">筛选条件</h2>
              {hasActiveFilter && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="text-xs font-medium text-accent-600 transition-colors hover:text-accent-700"
                >
                  清除筛选
                </button>
              )}
            </div>
            <FilterGroup
              title="地区"
              options={REGION_OPTIONS}
              value={filters.region}
              onChange={(value) => updateFilter("region", value)}
            />
            <FilterGroup
              title="年份"
              options={YEAR_OPTIONS}
              value={filters.year}
              onChange={(value) => updateFilter("year", value)}
            />
            <FilterGroup
              title="类型"
              options={GENRE_OPTIONS}
              value={filters.genre}
              onChange={(value) => updateFilter("genre", value)}
            />
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-600">
              共 <span className="font-semibold text-foreground-900">{sorted.length}</span> 部内容
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPanelOpen(true)}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 lg:hidden"
              >
                <i className="ri-filter-3-line" />
                筛选
              </button>
              <SortDropdown value={sortKey} onChange={setSortKey} />
            </div>
          </div>

          {paged.length > 0 ? (
            <>
              <VideoGrid videos={paged} />
              <Pagination current={page} total={total} onChange={setPage} />
            </>
          ) : (
            <EmptyState
              icon="ri-search-eye-line"
              title="没有符合条件的内容"
              description="当前的筛选条件下没有匹配结果，试试放宽条件或清除筛选。"
              action={
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
                >
                  <i className="ri-refresh-line" />
                  清除筛选条件
                </button>
              }
            />
          )}
        </div>
      </div>

      {panelOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setPanelOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-background-50 p-5">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="font-heading text-base font-bold text-foreground-950">筛选条件</h2>
              <button
                type="button"
                onClick={() => setPanelOpen(false)}
                aria-label="关闭筛选"
                className="flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-700"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="space-y-6">
              <FilterGroup
                title="地区"
                options={REGION_OPTIONS}
                value={filters.region}
                onChange={(value) => updateFilter("region", value)}
              />
              <FilterGroup
                title="年份"
                options={YEAR_OPTIONS}
                value={filters.year}
                onChange={(value) => updateFilter("year", value)}
              />
              <FilterGroup
                title="类型"
                options={GENRE_OPTIONS}
                value={filters.genre}
                onChange={(value) => updateFilter("genre", value)}
              />
            </div>
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={resetFilters}
                className="flex-1 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700"
              >
                清除筛选
              </button>
              <button
                type="button"
                onClick={() => setPanelOpen(false)}
                className="flex-1 whitespace-nowrap rounded-md bg-primary-600 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                查看结果
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}